/**
 * Générateur — Comprendre le sens de la multiplication de deux décimaux
 * Attendu ID : 612 | Niveau : 6ème | Domaine : NC
 *
 * Types générés :
 *   - sensmult_qcm_unique          (déduire un produit décimal d'un produit entier)
 *   - sensmult_texte               (saisie d'aire d'un rectangle via SVG inline)
 *   - sensmult_relier              (relier produits décimaux issus d'un même couple à leurs résultats)
 *   - sensmult_tableau_vf          (V/F sur déductions à partir d'un produit entier annoncé)
 *   - sensmult_texte_trou_drag     (cascade de déductions à compléter)
 *
 * Règles dures de la fiche cluster 8 (section ID 612) — sur un lot de 12 ex. :
 *   - ≥ 3 cas (A × 0,1, B × 0,1) → résultat à 2 rangs décimaux (décalage total 2)
 *   - ≥ 3 cas (A × 0,01, B × 0,1) OU (A × 0,1, B × 0,01) → décalage total 3
 *   - ≥ 2 cas (A × 0,01, B × 0,01) → décalage total 4
 *   - ≥ 1 cas où le produit « retombe » à l'entier ou à 1 décimale
 *   - ≥ 1 cas hors « A = B »
 *
 * Mécanismes-clés testés :
 *   - Décalage de virgule = somme des chiffres décimaux des facteurs
 *   - Sens via l'aire d'un rectangle
 *   - Lecture de résultats équivalents (ex : 107,3 / 107,30)
 *   - Retombée à l'entier (ex : 25×8=200 → 2,5×0,8=2)
 *
 * Variantes estimées : > 4000
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeRelier, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 612;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Labels ───
const L_QCM = 'Déduire un produit décimal à partir d\'un produit entier';
const L_SAISIE_AIRE = 'Calculer l\'aire d\'un rectangle à dimensions décimales';
const L_RELIER = 'Relier des produits décimaux à leurs résultats (même couple)';
const L_VF = 'Vrai/Faux sur des déductions de produits décimaux';
const L_TROU_DRAG = 'Compléter une cascade de déductions (drag)';

// ─── Helpers numériques (string-based, évite IEEE 754) ───

/**
 * Retire les zéros de fin d'une représentation décimale française.
 * Ex : "107,30" → "107,3" ; "200,00" → "200" ; "0,1073" → "0,1073"
 */
const stripZeros = (s) => {
    if (!s.includes(',')) return s;
    let t = s.replace(/0+$/, '');
    if (t.endsWith(',')) t = t.slice(0, -1);
    return t;
};

/**
 * Nombre de chiffres décimaux d'un décimal en écriture française.
 * "3,14" → 2 ; "45" → 0 ; "0,1" → 1 ; "25" → 0
 */
const decCount = (s) => {
    const [, dec = ''] = String(s).split(',');
    return dec.length;
};

/**
 * Produit de deux décimaux en écriture française, en passant par les entiers
 * (pour être exact). Retourne une string française.
 * Ex : mulDec("3,7", "2,9") → "10,73"
 *      mulDec("2,5", "0,8") → "2"
 *      mulDec("37", "29")   → "1073"
 */
const mulDec = (a, b) => {
    const da = decCount(a);
    const db = decCount(b);
    const ai = parseInt(String(a).replace(/[\s,]/g, ''), 10);
    const bi = parseInt(String(b).replace(/[\s,]/g, ''), 10);
    const prod = ai * bi;
    const totalDec = da + db;
    if (totalDec === 0) return String(prod);
    let p = String(prod);
    // Pad à gauche si nécessaire pour qu'il y ait au moins totalDec+1 chiffres
    while (p.length <= totalDec) p = '0' + p;
    const intPart = p.slice(0, p.length - totalDec);
    const decPart = p.slice(p.length - totalDec);
    const intTrim = intPart.replace(/^0+/, '') || '0';
    const decTrim = decPart.replace(/0+$/, '');
    if (decTrim === '') return intTrim;
    return `${intTrim},${decTrim}`;
};

/**
 * Décale la virgule d'un décimal de k rangs vers la gauche.
 * Ex : shiftLeft("1073", 2) → "10,73"
 *      shiftLeft("1073", 4) → "0,1073"
 */
const shiftLeft = (numStr, k) => {
    const clean = String(numStr).replace(/\s/g, '');
    const [intPart, decPart = ''] = clean.split(',');
    const allDigits = intPart + decPart;
    const newDecs = decPart.length + k;
    const totalLen = allDigits.length;
    if (newDecs < totalLen) {
        const newInt = allDigits.slice(0, totalLen - newDecs);
        const newDec = allDigits.slice(totalLen - newDecs);
        const newIntTrim = newInt.replace(/^0+/, '') || '0';
        const newDecTrim = newDec.replace(/0+$/, '');
        if (newDecTrim === '') return newIntTrim;
        return `${newIntTrim},${newDecTrim}`;
    }
    const zeros = '0'.repeat(newDecs - totalLen);
    const rawDec = zeros + allDigits;
    const decTrim = rawDec.replace(/0+$/, '');
    if (decTrim === '') return '0';
    return `0,${decTrim}`;
};

/** Compare deux strings comme décimaux (4,5 ≡ 4,50). */
const sameDecimal = (a, b) => {
    const toNum = (s) => parseFloat(String(s).replace(',', '.'));
    return Math.abs(toNum(a) - toNum(b)) < 1e-12;
};

// ─── Pools de couples d'entiers canoniques ───
// Remplacé par genCoupleEntier() (~684 couples dynamiques) — pool COUPLES supprimé.

/**
 * Génère dynamiquement un couple entier [A, B, A*B] pour les exercices "sens mult".
 * Plage pédagogique : A ∈ [12,49], B ∈ [12,29]. Exclut multiples de 10 et A=B.
 * ~684 couples possibles (bien plus que le pool curé COUPLES).
 */
const genCoupleEntier = () => {
    for (let i = 0; i < 20; i++) {
        const A = randInt(12, 49);
        const B = randInt(12, 29);
        if (A % 10 === 0 || B % 10 === 0) continue; // éviter décalage trivial ×10
        if (A === B) continue;
        return [String(A), String(B), String(A * B)];
    }
    return ['37', '29', '1073']; // fallback canonique du cours
};

/**
 * Génère dynamiquement une paire (L, l) pour aire_rectangle_texte.
 * L ∈ [2,0 ; 9,5], l ∈ [1,0 ; 5,0] — 1 décimale chacun → aire ≤ 2 décimales après stripZeros.
 */
const genPaireAireDynamique = () => {
    const L10 = randInt(20, 95);  // 2,0 → 9,5
    let l10 = randInt(10, 50);    // 1,0 → 5,0
    // Longueur > largeur toujours (revue 2026-08-27) : ~15 % des tirages
    // inversaient les deux (« longueur 2,3 » plus courte que « largeur 2,9 »).
    while (l10 >= L10) l10 = randInt(10, 50);
    const L = `${Math.floor(L10 / 10)},${L10 % 10}`;
    const l = `${Math.floor(l10 / 10)},${l10 % 10}`;
    return [L, l];
};

/**
 * Décline un couple (A, B, P) en ses 5 écritures décimales principales,
 * où chaque écriture est obtenue en décalant la virgule de Da/Db rangs
 * pour chaque facteur. Retourne la liste des variantes possibles :
 *   { aStr, bStr, decA, decB, resStr, decRes }
 * avec decA ∈ {0,1,2} et decB ∈ {0,1,2}.
 */
const declineCouple = (couple) => {
    const [A, B, P] = couple;
    const variantes = [];
    for (let da = 0; da <= 2; da++) {
        for (let db = 0; db <= 2; db++) {
            const aStr = shiftLeft(A, da);
            const bStr = shiftLeft(B, db);
            const resStr = shiftLeft(P, da + db);
            variantes.push({ aStr, bStr, decA: da, decB: db, resStr, decRes: da + db });
        }
    }
    return variantes;
};

// ═══════════════════════════════════════════════════════════
// 1. QCM UNIQUE — Déduire un produit décimal d'un produit entier
// ═══════════════════════════════════════════════════════════

const FORMULATIONS_QCM = [
    (A, B, P, aStr, bStr) => `<p>On sait que ${A} × ${B} = ${formatThousand(P)}. Combien fait ${aStr} × ${bStr} ?</p>`,
    (A, B, P, aStr, bStr) => `<p>Sachant que ${A} × ${B} = ${formatThousand(P)}, calcule ${aStr} × ${bStr}.</p>`,
    (A, B, P, aStr, bStr) => `<p>À partir de l'égalité ${A} × ${B} = ${formatThousand(P)}, déduis ${aStr} × ${bStr}.</p>`,
];

/** Ajoute des espaces de milliers (pour l'affichage de P entier > 999). */
const formatThousand = (s) => String(s).replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f');

const genSensMultQcm = () => {
    const couple = genCoupleEntier();
    const [A, B, P] = couple;
    // Piocher une variante où au moins un facteur est décimal
    const variantes = declineCouple(couple).filter((v) => v.decA + v.decB >= 1);
    const v = randomChoice(variantes);
    const correct = v.resStr;

    // Distracteurs : 3 familles
    //   (D1) décalage trop court (k - 1)
    //   (D2) décalage trop long (k + 1)
    //   (D3) résultat entier (P sans virgule), avec formatage milliers
    const dist = new Set();
    if (v.decRes >= 1) dist.add(shiftLeft(P, Math.max(0, v.decRes - 1)));
    if (v.decRes <= 3) dist.add(shiftLeft(P, v.decRes + 1));
    dist.add(P); // entier brut

    // Nettoyer collisions avec la bonne réponse
    const arr = [...dist].filter((x) => !sameDecimal(x, correct));
    while (arr.length < 3) {
        const extra = shiftLeft(P, Math.min(5, v.decRes + 2 + arr.length));
        if (!sameDecimal(extra, correct) && !arr.some((x) => sameDecimal(x, extra))) arr.push(extra);
        else arr.push(`${P}0`);
    }

    return makeQcm(
        [correct, ...arr.slice(0, 3)],
        correct,
        randomChoice(FORMULATIONS_QCM)(A, B, P, v.aStr, v.bStr),
        opts(L_QCM),
    );
};

// ═══════════════════════════════════════════════════════════
// 2. SAISIE AIRE — Rectangle à dimensions décimales (SVG inline)
// ═══════════════════════════════════════════════════════════

// PAIRES_AIRE remplacé par genPaireAireDynamique() (~3000 couples dynamiques).

/**
 * Construit un data URL SVG d'un rectangle avec ses dimensions L × l en cm.
 * On suit le pattern de gen_601 (fractionRectsSvg) : SVG string → encodeURIComponent → data URL.
 * Le SVG est ensuite passé via elements_additionnels avec type: 'image' pour que le
 * renderer le charge via un <img src=...> classique (sinon sanitizeHtml le strippe).
 */
// Variété d'unités (revue 2026-08-27, demande prof) : l'aire s'exprime dans
// l'unité carrée correspondante (mm² … km²), affichée en suffixe de saisie.
const UNITES_AIRE = ['mm', 'cm', 'dm', 'm', 'km'];

const buildRectDataUrl = (L, l, u) => {
    // Rectangle "peint" jaune avec dimensions — couleurs claires pour être lisibles
    // sur le fond dark-mode du container ImageElementEleve (bg-slate-800/50).
    // Convention math : longueur au-dessus, largeur à gauche, alignées sur les côtés.
    const fill = '#fef3c7';    // amber-100 (effet peint)
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 280 180" width="280" height="180"><rect x="60" y="50" width="180" height="90" fill="${fill}" stroke="currentColor" stroke-width="2"/><text x="150" y="38" text-anchor="middle" font-size="18" fill="currentColor" font-weight="600">${L} ${u}</text><text x="32" y="100" text-anchor="middle" font-size="18" fill="currentColor" font-weight="600">${l} ${u}</text></svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

// Anti-répétition immédiate (revue 2026-08-20, recette standard).
let derniereSignatureAire612 = null;

const genAireRectangleSaisie = () => {
    let payload = construireAireRectangleSaisie();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureAire612; t++) {
        payload = construireAireRectangleSaisie();
    }
    derniereSignatureAire612 = payload.texte;
    return payload;
};

const construireAireRectangleSaisie = () => {
    // Génération dynamique : L ∈ [2,0 ; 9,5], l ∈ [1,0 ; 5,0] → ~3000 couples
    const [L, l] = genPaireAireDynamique();
    const u = randomChoice(UNITES_AIRE);
    const aire = mulDec(L, l);
    const elAdd = {
        version: 1,
        elements: [{ type: 'image', config: { imageUrl: buildRectDataUrl(L, l, u), maxWidth: 'small' } }],
    };
    const texte = `<p>Voici un rectangle de longueur <strong>${L} ${u}</strong> et de largeur <strong>${l} ${u}</strong>.</p>{{element:0}}<p>Quelle est son aire ?</p>`;
    const payload = makeSaisie(aire, texte, {
        ...opts(L_SAISIE_AIRE),
        strict: false,
        suffixe: `${u}²`,
        elements_additionnels: elAdd,
    });
    // Calculatrice RETIRÉE (décision prof 2026-08-31) : multiplication de
    // deux décimaux à 1 décimale, à poser — technique au programme de 6e.
    return payload;
};

// ═══════════════════════════════════════════════════════════
// 3. RELIER — 4 variantes décimales d'un même couple entier
// ═══════════════════════════════════════════════════════════

// Anti-répétition immédiate (revue 2026-08-20, recette standard).
let derniereSignatureRelier612 = null;

const genSensMultRelier = () => {
    let payload = construireSensMultRelier();
    const sig = (p) => p.contenu.pairs.map((x) => x.source).join('|');
    for (let t = 0; t < 8 && sig(payload) === derniereSignatureRelier612; t++) {
        payload = construireSensMultRelier();
    }
    derniereSignatureRelier612 = sig(payload);
    return payload;
};

const construireSensMultRelier = () => {
    // Piocher un couple et 4 variantes aux RÉSULTATS DISTINCTS
    let selected = null;
    for (let tries = 0; tries < 30; tries++) {
        const couple = genCoupleEntier();   // dynamique : ~684 couples possibles
        const [A, B, P] = couple;
        const variantes = declineCouple(couple).filter((v) => v.decA + v.decB >= 1);
        // Grouper par résultat unique
        const parResultat = new Map();
        for (const v of variantes) {
            if (!parResultat.has(v.resStr)) parResultat.set(v.resStr, v);
        }
        const distinctes = shuffle([...parResultat.values()]);
        if (distinctes.length >= 4) {
            selected = { couple, variantes: distinctes.slice(0, 4) };
            break;
        }
    }

    // Fallback déterministe : couple (37, 29)
    if (!selected) {
        const couple = ['37', '29', '1073'];
        selected = {
            couple,
            variantes: [
                { aStr: '3,7',  bStr: '2,9',  resStr: '10,73'  },
                { aStr: '37',   bStr: '2,9',  resStr: '107,3'  },
                { aStr: '3,7',  bStr: '0,29', resStr: '1,073'  },
                { aStr: '0,37', bStr: '0,29', resStr: '0,1073' },
            ],
        };
    }

    const pairs = selected.variantes.map((v) => ({
        source: `${v.aStr} × ${v.bStr}`,
        target: v.resStr,
    }));
    const [A, B, P] = selected.couple;

    return makeRelier(
        pairs,
        `<p>On sait que ${A} × ${B} = ${formatThousand(P)}. Relie chaque produit décimal à son résultat.</p>`,
        opts(L_RELIER),
    );
};

// ═══════════════════════════════════════════════════════════
// 4. TABLEAU V/F — Déductions 3V/3F à partir d'un couple entier
// ═══════════════════════════════════════════════════════════

const genSensMultVF = () => {
    // Choisir un couple et construire 6 lignes 3V/3F
    let couple = genCoupleEntier();
    let [A, B, P] = couple;
    let variantes = declineCouple(couple).filter((v) => v.decA + v.decB >= 1);

    // Si le couple n'a pas assez de variantes uniques, en prendre un autre
    let tries = 0;
    while (variantes.length < 4 && tries < 10) {
        couple = genCoupleEntier();
        [A, B, P] = couple;
        variantes = declineCouple(couple).filter((v) => v.decA + v.decB >= 1);
        tries++;
    }

    const shuffled = shuffle(variantes);

    // Quota variable de lignes vraies (revue 2026-08-20) : 2 à 4, pour empêcher
    // la stratégie de comptage « toujours 3 Vrai / 3 Faux ».
    const nbVraiCible = randInt(2, 4);
    const nbFauxCible = 6 - nbVraiCible;

    const vrais = shuffled.slice(0, nbVraiCible);
    const lignes = [];
    const bonnes = [];
    const seen = new Set();
    const nbFaux = () => bonnes.filter((b) => b === 1).length;

    const pushLigne = (text, isVrai) => {
        if (seen.has(text)) return false;
        seen.add(text);
        lignes.push(text);
        bonnes.push(isVrai ? 0 : 1);
        return true;
    };

    vrais.forEach((v) => {
        pushLigne(`${v.aStr} × ${v.bStr} = ${v.resStr}`, true);
    });

    // Lignes FAUSSES — 3 familles distinctes, ordre mélangé pour que la famille
    // sacrifiée (quand nbFauxCible = 2) change d'un tirage à l'autre :
    //   (F1) décalage trop court
    //   (F2) décalage trop long
    //   (F3) virgule absente (résultat = P entier)
    const fauxSources = shuffle(variantes).slice(0, 3);

    const familles = shuffle([
        () => { // F1 : décalage trop court
            for (const v of fauxSources) {
                if (v.decRes >= 1) {
                    const faux = shiftLeft(P, Math.max(0, v.decRes - 1));
                    if (!sameDecimal(faux, v.resStr) && pushLigne(`${v.aStr} × ${v.bStr} = ${faux}`, false)) break;
                }
            }
        },
        () => { // F2 : décalage trop long
            for (const v of fauxSources) {
                const faux = shiftLeft(P, v.decRes + 1);
                if (!sameDecimal(faux, v.resStr) && pushLigne(`${v.aStr} × ${v.bStr} = ${faux}`, false)) break;
            }
        },
        () => { // F3 : virgule absente (résultat = P entier)
            for (const v of fauxSources) {
                if (v.decRes >= 1 && !sameDecimal(P, v.resStr) && pushLigne(`${v.aStr} × ${v.bStr} = ${P}`, false)) break;
            }
        },
    ]);
    familles.forEach((f) => { if (nbFaux() < nbFauxCible) f(); });

    // Équilibrage : compléter jusqu'à 6 lignes en respectant les quotas tirés
    // (retente sur collision au lieu d'abandonner).
    let garde = 0;
    while (lignes.length < 6 && garde < 40) {
        garde++;
        const vraiCount = bonnes.filter((b) => b === 0).length;
        const v = randomChoice(variantes);
        if (vraiCount < nbVraiCible) {
            pushLigne(`${v.aStr} × ${v.bStr} = ${v.resStr}`, true);
        } else {
            const faux = shiftLeft(P, v.decRes + randInt(1, 2));
            if (!sameDecimal(faux, v.resStr)) pushLigne(`${v.aStr} × ${v.bStr} = ${faux}`, false);
        }
    }

    // Mélanger les lignes
    const indexed = lignes.map((l, i) => ({ l, b: bonnes[i] }));
    const shuf = shuffle(indexed);
    const finalLignes = shuf.map((x) => x.l);
    const finalBonnes = shuf.map((x) => x.b);

    return makeVraiFaux(
        finalLignes,
        finalBonnes,
        `<p>On sait que ${A} × ${B} = ${formatThousand(P)}. Pour chaque égalité, dis si elle est vraie ou fausse.</p>`,
        opts(L_VF),
    );
};

// ═══════════════════════════════════════════════════════════
// 5. TEXTE À TROUS (DRAG) — Cascade de déductions
// ═══════════════════════════════════════════════════════════

// Anti-répétition immédiate (revue 2026-08-20, recette standard).
let derniereSignatureDrag612 = null;

const genSensMultTrouDrag = () => {
    let payload = construireSensMultTrouDrag();
    for (let t = 0; t < 8 && payload.contenu.phrase === derniereSignatureDrag612; t++) {
        payload = construireSensMultTrouDrag();
    }
    derniereSignatureDrag612 = payload.contenu.phrase;
    return payload;
};

const construireSensMultTrouDrag = () => {
    // Piocher un couple et 3 variantes aux décalages croissants et résultats distincts
    let couple = genCoupleEntier();
    let [A, B, P] = couple;
    let variantes = declineCouple(couple).filter((v) => v.decA + v.decB >= 1);

    // On cherche 3 variantes avec des decRes DIFFÉRENTS (progression)
    const parDec = new Map();
    for (const v of variantes) {
        if (!parDec.has(v.decRes)) parDec.set(v.decRes, v);
    }
    let slots = shuffle([...parDec.values()]).slice(0, 3);
    // Trier par décalage croissant pour la progression
    slots.sort((a, b) => a.decRes - b.decRes);

    // Fallback
    if (slots.length < 3) {
        const [a2, b2, p2] = ['37', '29', '1073'];
        A = a2; B = b2; P = p2;
        slots = [
            { aStr: '37',   bStr: '2,9',  resStr: '107,3',  decRes: 1 },
            { aStr: '3,7',  bStr: '2,9',  resStr: '10,73',  decRes: 2 },
            { aStr: '0,37', bStr: '0,29', resStr: '0,1073', decRes: 4 },
        ];
    }

    const phrase = slots.map((v) => `${v.aStr} × ${v.bStr} = {{${v.resStr}}}`).join('\n');

    // Distracteurs : 3 familles d'erreurs didactiques réelles
    //   (D1) décalage trop court sur le 1er slot (virgule trop à droite)
    //   (D2) décalage trop long sur le 2e slot (virgule trop à gauche)
    //   (D3) virgule manquante (résultat = P entier)
    //
    // IMPORTANT : jamais de « zéro de fin » (107,30 ≡ 107,3) car
    // mathématiquement équivalent → rendrait l'exercice ambigu en drag.
    const dist = new Set();
    const bonnes = new Set(slots.map((s) => s.resStr));
    const isEquivalent = (candidate) => {
        // Compare numériquement (via sameDecimal qui tolère les erreurs IEEE 754)
        for (const b of bonnes) {
            if (sameDecimal(candidate, b)) return true;
        }
        return false;
    };
    const addIfNew = (x) => {
        if (x && !dist.has(x) && !isEquivalent(x)) dist.add(x);
    };

    // D1 : décalage trop court sur 1er slot
    const s0 = slots[0];
    if (s0.decRes >= 1) addIfNew(shiftLeft(P, Math.max(0, s0.decRes - 1)));

    // D2 : décalage trop long sur 2e slot (si possible)
    const s1 = slots[1] || slots[0];
    addIfNew(shiftLeft(P, s1.decRes + 1));

    // D3 : virgule manquante (résultat = P entier brut)
    addIfNew(P);

    // Compléter à 3 si besoin (décalages aléatoires non-équivalents)
    let safety = 0;
    while (dist.size < 3 && safety < 20) {
        safety++;
        const s = randomChoice(slots);
        const wrongK = s.decRes + randInt(1, 3);
        addIfNew(shiftLeft(P, wrongK));
    }
    // Ultime fallback : pool hardcodé si jamais distincts
    const fallbackPool = [P, shiftLeft(P, 1), shiftLeft(P, 3), shiftLeft(P, 4)];
    let fbIdx = 0;
    while (dist.size < 3 && fbIdx < fallbackPool.length) {
        addIfNew(fallbackPool[fbIdx]);
        fbIdx++;
    }

    return makeTexteTrou(
        phrase,
        `<p>On sait que ${A} × ${B} = ${formatThousand(P)}. Complète la cascade en faisant glisser les bons résultats.</p>`,
        { ...opts(L_TROU_DRAG), mode: 'drag', distracteurs: [...dist].slice(0, 3), strict: false },
    );
};


// ═══════════════════════════════════════════════════════════
// 6. SENS (revue 2026-08-28, demande prof) — deux formats qui évaluent le
// SENS et non la technique : l'effet d'un changement de facteur sur le
// produit (structure), et le choix du bon modèle (répétition vs aire).
// ═══════════════════════════════════════════════════════════

const L_EFFET = "Effet d'un changement de facteur sur le produit (sans calculer)";
const L_MODELE = 'Choisir le bon modèle : répétition ou aire';

const numDec = (str) => parseFloat(String(str).replace(',', '.'));

// --- 6a. QCM « effet sur le produit » ---

const EFFETS_LIB = {
    div10:   'Il faut le diviser par 10',
    div100:  'Il faut le diviser par 100',
    div1000: 'Il faut le diviser par 1 000',
    mul10:   'Il faut le multiplier par 10',
    egal:    'Il ne change pas',
};

/** Les 5 transformations, tirées équitablement. */
const TRANSFOS_EFFET = [
    (A, B) => { // un facteur ÷ 10
        const surA = Math.random() < 0.5;
        const [nA, nB] = surA ? [shiftLeft(A, 1), B] : [A, shiftLeft(B, 1)];
        const rempl = surA ? `le ${A} par ${nA}` : `le ${B} par ${nB}`;
        return { nA, nB, effet: 'div10', rempl };
    },
    (A, B) => { // un facteur ÷ 100
        const surA = Math.random() < 0.5;
        const [nA, nB] = surA ? [shiftLeft(A, 2), B] : [A, shiftLeft(B, 2)];
        const rempl = surA ? `le ${A} par ${nA}` : `le ${B} par ${nB}`;
        return { nA, nB, effet: 'div100', rempl };
    },
    (A, B) => { // les DEUX facteurs ÷ 10 (piège : beaucoup répondent ÷ 10)
        const nA = shiftLeft(A, 1);
        const nB = shiftLeft(B, 1);
        return { nA, nB, effet: 'div100', rempl: `le ${A} par ${nA} et le ${B} par ${nB}` };
    },
    (A, B) => { // compensation ×10 / ÷10 : produit inchangé
        const surA = Math.random() < 0.5;
        const [nA, nB] = surA ? [`${A}0`, shiftLeft(B, 1)] : [shiftLeft(A, 1), `${B}0`];
        return { nA, nB, effet: 'egal', rempl: `le ${A} par ${nA} et le ${B} par ${nB}` };
    },
    (A, B) => { // un facteur × 10
        const surA = Math.random() < 0.5;
        const [nA, nB] = surA ? [`${A}0`, B] : [A, `${B}0`];
        const rempl = surA ? `le ${A} par ${nA}` : `le ${B} par ${nB}`;
        return { nA, nB, effet: 'mul10', rempl };
    },
    (A, B) => { // ÷ 100 et ÷ 10 combinés : produit ÷ 1 000 (sinon ce
        // distracteur ne serait jamais la bonne réponse — mesure 2026-08-28)
        const surA = Math.random() < 0.5;
        const [nA, nB] = surA ? [shiftLeft(A, 2), shiftLeft(B, 1)] : [shiftLeft(A, 1), shiftLeft(B, 2)];
        return { nA, nB, effet: 'div1000', rempl: `le ${A} par ${nA} et le ${B} par ${nB}` };
    },
];

// Anti-répétition immédiate (recette standard).
let derniereSignatureEffet612 = null;

const genSensEffetQcm = () => {
    let payload = construireSensEffetQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureEffet612; t++) {
        payload = construireSensEffetQcm();
    }
    derniereSignatureEffet612 = payload.texte;
    return payload;
};

const construireSensEffetQcm = () => {
    const [A, B, P] = genCoupleEntier();
    const t = randomChoice(TRANSFOS_EFFET)(A, B);

    const correct = EFFETS_LIB[t.effet];
    // Distracteurs : les autres effets du pool (même gabarit, pas d'élimination
    // de forme possible) — on en retire un au hasard pour garder 3.
    const autres = Object.keys(EFFETS_LIB).filter((k) => k !== t.effet);
    const distracteurs = shuffle(autres).slice(0, 3).map((k) => EFFETS_LIB[k]);

    const texte = `<p>On sait que ${A} × ${B} = ${formatThousand(P)}.</p>`
        + `<p>On remplace ${t.rempl}. Pour trouver le résultat du produit ${t.nA} × ${t.nB}, `
        + `que doit-on faire avec le résultat de la multiplication ${A} × ${B} ?</p>`;

    return makeQcm([correct, ...distracteurs], correct, texte, opts(L_EFFET));
};

// --- 6b. Vrai/Faux « quel modèle fonctionne ? » ---

/** Décimal à 1 décimale NON NULLE (jamais entier) : « x,y », y ∈ [1;9]. */
const decUn = (intMin, intMax) => `${randInt(intMin, intMax)},${randInt(1, 9)}`;

// Anti-répétition immédiate (recette standard).
let derniereSignatureModele612 = null;

const genSensModeleVF = () => {
    let payload = construireSensModeleVF();
    for (let t = 0; t < 8 && JSON.stringify(payload.contenu) === derniereSignatureModele612; t++) {
        payload = construireSensModeleVF();
    }
    derniereSignatureModele612 = JSON.stringify(payload.contenu);
    return payload;
};

const construireSensModeleVF = () => {
    for (let essai = 0; essai < 30; essai++) {
        const lignes = [];
        const bonnes = [];
        const push = (l, vrai) => { lignes.push(l); bonnes.push(vrai ? 0 : 1); };

        // F1 — répétition, instance concrète.
        // VRAI : nombre ENTIER de répétitions (la répétition survit).
        // FAUX : deux décimaux — k × d2 = d1 × d2 exigerait k = d1 non entier.
        if (Math.random() < 0.5) {
            const a = randInt(3, 4);
            const d = decUn(2, 9);
            push(`Pour calculer ${a} × ${d}, on peut additionner ${Array(a).fill(d).join(' + ')}.`, true);
        } else {
            const d1 = decUn(2, 9);
            let d2 = decUn(2, 9);
            while (d2 === d1) d2 = decUn(2, 9);
            push(`En additionnant ${d2} plusieurs fois de suite, on peut obtenir exactement ${d1} × ${d2}.`, false);
        }

        // F2 — aire du rectangle : multiplication (VRAI) vs addition (FAUX).
        {
            const d1 = decUn(2, 9);
            let d2 = decUn(1, 5);
            // Gardes : côtés distincts, et somme ≠ produit pour la version
            // fausse — à TOLÉRANCE (mesure 2026-08-28 : avec ===, les
            // flottants laissaient passer 3,5 + 1,4 = 3,5 × 1,4 = 4,9).
            while (d2 === d1 || Math.abs((numDec(d1) + numDec(d2)) - numDec(d1) * numDec(d2)) < 1e-9) d2 = decUn(1, 5);
            if (Math.random() < 0.5) {
                push(`L'aire d'un rectangle de côtés ${d1} cm et ${d2} cm est ${d1} × ${d2} cm².`, true);
            } else {
                push(`L'aire d'un rectangle de côtés ${d1} cm et ${d2} cm est ${d1} + ${d2} cm².`, false);
            }
        }

        // F3 — taille du produit (un produit peut rapetisser).
        if (Math.random() < 0.5) {
            push('Un produit de deux nombres décimaux peut être plus petit que chacun de ses deux facteurs.', true);
        } else {
            push('Quand on multiplie deux nombres, le résultat est toujours plus grand que chacun des deux.', false);
        }

        // F4 — la règle de la frontière : c'est le nombre ENTIER de répétitions
        // qui compte, pas la présence d'une virgule.
        if (Math.random() < 0.5) {
            push("Quand aucun des deux facteurs n'est un nombre entier, on ne peut pas obtenir le produit en additionnant plusieurs fois l'un des deux facteurs.", true);
        } else {
            push("Dès qu'un des deux facteurs n'est pas un nombre entier, on ne peut plus obtenir le produit en additionnant plusieurs fois l'un des deux facteurs.", false);
        }

        // Quota : au moins 1 vrai ET 1 faux (sinon on retire le lot).
        const nbFaux = bonnes.filter((b) => b === 1).length;
        if (nbFaux === 0 || nbFaux === 4) continue;

        const indexed = shuffle(lignes.map((l, i) => ({ l, b: bonnes[i] })));
        return makeVraiFaux(
            indexed.map((x) => x.l),
            indexed.map((x) => x.b),
            '<p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            opts(L_MODELE),
        );
    }
    // Fallback improbable : lot canonique mélangé.
    return makeVraiFaux(
        [
            'Pour calculer 3 × 2,9, on peut additionner 2,9 + 2,9 + 2,9.',
            "L'aire d'un rectangle de côtés 3,7 cm et 2,9 cm est 3,7 + 2,9 cm².",
        ],
        [0, 1],
        '<p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
        opts(L_MODELE),
    );
};


// --- 6c. Texte à trous « de la répétition à l'aire » (revue 2026-08-28) ---

const L_SENS_TROU = "Compléter le raisonnement : de la répétition à l'aire";

// Anti-répétition immédiate (recette standard).
let derniereSignatureSensTrou612 = null;

const genSensModeleTrou = () => {
    let payload = construireSensModeleTrou();
    for (let t = 0; t < 8 && payload.contenu.phrase === derniereSignatureSensTrou612; t++) {
        payload = construireSensModeleTrou();
    }
    derniereSignatureSensTrou612 = payload.contenu.phrase;
    return payload;
};

const construireSensModeleTrou = () => {
    const n = randInt(3, 5);            // nombre de répétitions (somme écrite en entier)
    const d = decUn(2, 9);              // terme répété (1 décimale non nulle)
    let e = decUn(2, 9);                // 2e facteur du produit qui « casse »
    while (e === d) e = decUn(2, 9);

    const somme = Array(n).fill(d).join(' + ');
    // Pas de paire de trous autour d'un opérateur (règle A1 commutative) :
    // chaque trou est seul dans son expression, l'autre opérande est en clair.
    const phrase = `Le nombre ${d} est répété {{${n}}} fois dans la somme ${somme}.
`
        + `Cette somme est donc égale au produit ${n} × {{${d}}}.
`
        + `Pour le produit ${e} × ${d}, l'addition répétée ne fonctionne plus : `
        + `il faudrait répéter ${d} exactement {{${e}}} fois, et ce n'est pas un nombre entier.
`
        + `Mais ${e} × ${d} représente encore l'aire d'un rectangle de longueur {{${e}}} et de largeur ${d}.`;

    return makeTexteTrou(phrase, '<p>Complète le raisonnement.</p>', {
        ...opts(L_SENS_TROU),
        mode: 'saisie',
        distracteurs: [],
        strict: false,
    });
};

// ─── Registre ───
const GENERATORS = {
    sensmult_qcm_unique: genSensMultQcm,
    sensmult_relier: genSensMultRelier,
    sensmult_tableau_vf: genSensMultVF,
    sensmult_texte_trou_drag: genSensMultTrouDrag,
    aire_rectangle_texte: genAireRectangleSaisie,

    // Formats « sens » (revue 2026-08-28) : la propriété et le modèle.
    sens_effet_qcm_unique: genSensEffetQcm,
    sens_modele_tableau_vf: genSensModeleVF,
    sens_modele_texte_trou_saisie: genSensModeleTrou,
};

// ─── Catégories ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    // Revue 2026-08-28 : la carte historique évaluait la TECHNIQUE du décalage,
    // pas le sens — renommée en conséquence. Le sens (propriété + modèle) a
    // désormais sa propre carte.
    {
        id: 'sens_multiplication',
        label: 'Sens de la multiplication de décimaux',
        formatsList: [...buildFormats('sens_modele'), ...buildFormats('sens_effet')],
    },
    { id: 'sensmult', label: 'Du produit entier au produit décimal (placer la virgule)', formatsList: buildFormats('sensmult') },
    // Libellé revu 2026-08-28 (choix prof) : la carte porte le CHANGEMENT de
    // modèle (répétition → aire), pas seulement le calcul d'aire.
    { id: 'aire_rectangle', label: "Quand la répétition ne suffit plus : l'aire du rectangle", formatsList: buildFormats('aire_rectangle') },
];

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
